import React, { useEffect, useMemo, useState } from 'react'
import { indexBy } from '@cicero/data/utils'
import { IconLabel, Loader, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Timestamp, cn, makeStyles, useObservable } from '@cicero/ui'
import { AttributeSchema, AttributeValueDoc, AttributeValueSchema, PageDoc, useModel } from '../../models'
import { PageLink, PageLinkRenderer } from 'views/PageLink'
import { AttributeValue, TemplateIdRenderer } from 'forms'
import { UserTile } from 'user'

/**
 * Renders a table of pages
 */
export const TableLayout = ({ pages, isLoading }: { pages: PageDoc[], isLoading?: boolean }) => {
  const { tableEl, tableCol, headerCol, titleCol, tableRow } = styles()
  const coreAttributes = useMemo<IPageTableColumn[]>(() => [
    { id: 'title', label: 'Title', icon: 'text_fields', className: titleCol, renderer: (p) => <PageLink page={p} icon quickOpen /> },
    { id: 'template', label: 'Template', icon: 'vertical_split', renderer: (p) => <TemplateIdRenderer id={p.template_id} /> },
    { id: 'created_at', label: 'Created', icon: 'schedule', renderer: (p) => <Timestamp date={p.created_at} /> },
    { id: 'updated_at', label: 'Updated', icon: 'schedule', renderer: (p) => <Timestamp date={p.updated_at} /> },
    { id: 'inbox_status', label: 'Inbox Status', icon: 'label', renderer: (p) => p.inbox_status },
    { id: 'inbox_due', label: 'Inbox Due', icon: 'schedule', renderer: (p) => <Timestamp date={p.inbox_date} /> },
    { id: 'inbox_assignee', label: 'Inbox Assignee', icon: 'person',
      renderer: (p) => p.inbox_assignee_id ? <UserTile id={p.inbox_assignee_id} size={30} /> : <></>,
    },
    { id: 'parent', label: 'Parent', icon: 'account_tree', className: titleCol,
      renderer: (p) => p.parent_id ? <PageLinkRenderer id={p.parent_id} /> : <></>,
    },
  ], [])
  const [columns, setColumns] = useState<IPageTableColumn[]>(coreAttributes)
  const [valueLookup, setValueLookup] = useState<{ [key: string]: AttributeValueDoc[] }>({})
  const valueCacheKey = (page: PageDoc, id: string) => `${page.id}:${id}`
  const Attributes = useModel(AttributeSchema)
  const AttributeValues = useModel(AttributeValueSchema)
  const attrs = useObservable(() => Attributes.QUERY$(), [], [])

  useEffect(() => {
    setColumns([
      ...coreAttributes.slice(0,2),
      ...attrs.map(attr => ({
        id: attr.id,
        icon: AttributeSchema.TYPE_CONFIG[attr.type].icon,
        label: attr.label,
        renderer: (p: PageDoc, v: AttributeValueDoc[]) => <AttributeValue key={attr.id} attribute={attr} values={v || []} compact />,
      })),
      ...coreAttributes.slice(2, coreAttributes.length),
    ])
  }, [attrs])

  useEffect(() => {
    const sub = AttributeValues.QUERY$(AttributeValueSchema.QUERIES.attrs(
      attrs.map(attr => attr.id),
      pages.map(page => page.id)
    )).subscribe((values) => {
      setValueLookup(indexBy(values, (val) => `${val.page_id}:${val.attribute_id}`))
    })

    return () => sub.unsubscribe()
  }, [attrs, pages])

  return (
    <TableContainer component={Paper}>
      { isLoading ? <Loader /> : null }
      <Table className={tableEl} aria-label="simple table" size="small" stickyHeader>
        <TableHead>
          <TableRow>
            { columns.map(col => <TableCell key={col.id} className={cn(headerCol, col.className||'')}>
              <IconLabel label={col.label} icon={col.icon} />
            </TableCell>) }
          </TableRow>
        </TableHead>
        <TableBody>
          {pages.map((page) => (
            <TableRow key={page.id} className={tableRow}>
              { columns.map(col => {
                const key = valueCacheKey(page, col.id)
                return <TableCell key={col.id} className={tableCol}>
                  {col.renderer ? col.renderer(page, valueLookup[key]) : null}
                </TableCell>
              }) }
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export interface IPageTableColumn {
  id: string
  icon?: string
  label: string
  className?: string
  renderer?(page: PageDoc, values?: AttributeValueDoc[]): any
}

const styles = makeStyles({
  headerCol: {
    minWidth: 100,
    whiteSpace: 'nowrap',
    borderRight: '1px solid #DDD',
    color: '#BBB',
  },
  tableCol: {
    maxWidth: 300,
  },
  titleCol: {
    minWidth: 350,
  },
  tableRow: {
    transition: 'background ease-in-out .2s',
    '&:hover': {
      background: '#FAFAFA',
    },
    // '&:nth-of-type(odd)': {
    //   background: '#EEE',
    // },
  },
  tableEl: {
    '& pre': {
      maxWidth: '300px',
      overflow: 'auto',
    },
  },
})
